<template>
  <div v-if="isPermissionDenied" class="permission-denied">
    <div class="permission-denied-content">
      <h2>{{ $t('common.no_permission') }}</h2>
      <p>{{ $t('audit.no_permission_message') }}</p>
    </div>
  </div>
  <div v-else class="auditReportContainer">
    <!-- Loading Spinner - Transparent Overlay -->
    <div v-if="loading" class="loading-overlay">
      <div class="spinner-border text-primary spinner-grow-xl" role="status"></div>
    </div>

    <!-- Filters and Content -->
    <div class="auditContent">
      <!-- Filter Controls -->
      <div class="auditFiltersContainer">
        <div class="filterRow">
          <div class="filterGroup">
            <label class="filterLabel">{{ $t('audit.jobId') }}</label>
            <input type="text" class="filterInput" :placeholder="$t('audit.jobIdPlaceholder')" v-model="filters.jobId" @input="handleFilterChange" />
          </div>
          <div class="filterGroup">
            <label class="filterLabel">{{ $t('audit.fileName') }}</label>
            <input type="text" class="filterInput" :placeholder="$t('audit.fileNamePlaceholder')" v-model="filters.fileName" @input="handleFilterChange" />
          </div>
          <div class="filterGroup">
            <label class="filterLabel">{{ $t('audit.resultDocument') }}</label>
            <input type="text" class="filterInput" :placeholder="$t('audit.resultDocumentPlaceholder')" v-model="filters.resultDocument" @input="handleFilterChange" />
          </div>
        </div>

        <div class="filterRow">
          <div class="filterGroup">
            <label class="filterLabel">{{ $t('audit.fromDate') }}</label>
            <input type="date" class="filterInput" v-model="filters.fromDate" @change="handleFilterChange" />
          </div>
          <div class="filterGroup">
            <label class="filterLabel">{{ $t('audit.toDate') }}</label>
            <input type="date" class="filterInput" v-model="filters.toDate" @change="handleFilterChange" />
          </div>
          <div class="filterGroup">
            <label class="filterLabel">{{ $t('audit.action') }}</label>
            <div class="dropdownWrapper">
              <select class="filterInput dropdownInput" v-model="filters.action" @change="handleFilterChange">
                <option value="">{{ $t('audit.actionPlaceholder') }}</option>
                <option v-for="action in actionOptions" :key="action.value" :value="action.value">
                  {{ $t(action.label) }}
                </option>
              </select>
              <span class="dropdownIcon">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M4 6L8 10L12 6" stroke="#8F969B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </span>
            </div>
          </div>
          <div class="filterButtonGroup">
            <button class="resetDateButton" @click="handleSearch">{{ $t('reports.search') }}</button>
            <button class="resetDateButton" @click="resetAllFilters">{{ $t('reports.reset') }}</button>
          </div>
        </div>
      </div>

      <!-- Replace your tableHeadingsNew section with this fixed version -->
      <div class="tableHeadingsNew">
        <div class="customRow">
          <!-- Navigation Controls -->
          <img src="/backward.svg" alt="Back" style="cursor: pointer" @click="goToPreviousPage" />

          <!-- Page Info -->
          <div class="pageDiv">
            <span class="pageDivText">{{ page }} / {{ totalPages }}</span>
          </div>

          <span class="pageOfTitle">{{ $t('common.page') }} {{ page }} {{ $t('common.of') }} {{ totalPages }}</span>

          <img src="/forward.svg" alt="Next" style="cursor: pointer" @click="goToNextPage" />

          <!-- Page Size Dropdown -->
          <div class="pageDiv" @click.stop="togglePageSizeDropdown">
            <span class="pageDivText">
              {{ pageSize }}
            </span>

            <img src="/arrowDownNew.png" class="arrowDownNew" alt="Arrow Down" />

            <!-- Dropdown Menu -->
            <ul
              ref="dropdownEl"
              v-if="showPageSizeDropdown"
              @click.stop
              :style="{
                position: 'absolute',
                top: 'calc(100% + 2px)',
                left: '0',
                right: '0',
                zIndex: '1000',
                background: '#fff',
                borderRadius: '6px',
                marginTop: '4px',
                listStyle: 'none',
                padding: '4px 0',
                minWidth: '100px',
                maxHeight: '200px',
                overflowY: 'auto',
                animation: 'dropdownFadeIn 0.15s ease-out',
              }"
            >
              <li
                v-for="size in pageSizeOptions"
                :key="size"
                @click.stop="selectPageSize(size)"
                :style="{
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontFamily: 'Archivo, sans-serif',
                  fontSize: '14px',
                  color: '#5c656c',
                  transition: 'background-color 0.15s ease',
                  userSelect: 'none',
                }"
                @mouseover="$event.target.style.backgroundColor = '#f0f0f0'"
                @mouseleave="$event.target.style.backgroundColor = 'transparent'"
              >
                {{ size }}
              </li>
            </ul>
          </div>

          <div class="download-info-note-inline">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
            <span>{{ $t('reports.download_note_short') }}</span>
          </div>
          <!-- Download Button -->
          <button :disabled="downloadingPDF" class="downloadExcelButton" @click="downloadAsPDF">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
              <polyline points="14,2 14,8 20,8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <line x1="10" y1="9" x2="8" y2="9" />
            </svg>
            {{ downloadingPDF ? $t('reports.downloading') : $t('reports.download_pdf') }}
          </button>
          <button class="downloadExcelButton" @click="downloadAsExcel">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
              <polyline points="14,2 14,8 20,8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <line x1="10" y1="9" x2="8" y2="9" />
            </svg>
            {{ downloadingExcel ? $t('reports.downloading') : $t('reports.download_excel') }}
          </button>
        </div>
      </div>

      <!-- Audit Table -->
      <div class="tableContainer" style="margin-top: 16px">
        <table class="customTable">
          <thead class="customTableHeader">
            <tr>
              <th class="col-job-id" :title="$t('audit.tableHeaders.jobId')">
                {{ $t('audit.tableHeaders.jobId') }}
              </th>
              <th class="col-action" :title="$t('audit.tableHeaders.action')">
                {{ $t('audit.tableHeaders.action') }}
              </th>
              <th class="col-action-details" :title="$t('audit.tableHeaders.actionDetails')">
                {{ $t('audit.tableHeaders.actionDetails') }}
              </th>
              <th class="col-file-name" :title="$t('audit.tableHeaders.fileName')">
                {{ $t('audit.tableHeaders.fileName') }}
              </th>
              <th class="col-result-doc" :title="$t('audit.tableHeaders.resultDocument')">
                {{ $t('audit.tableHeaders.resultDocument') }}
              </th>
              <th class="col-changed" :title="$t('audit.tableHeaders.changedValue')">
                {{ $t('audit.tableHeaders.changedValue') }}
              </th>
              <th class="col-action-by" :title="$t('audit.tableHeaders.actionBy')">
                {{ $t('audit.tableHeaders.actionBy') }}
              </th>
              <th class="col-action-date" :title="$t('audit.tableHeaders.actionDate')">
                {{ $t('audit.tableHeaders.actionDate') }}
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="audit in auditData" :key="audit.id" class="tableRow">
              <td class="col-job-id" :title="audit.job_id">
                {{ truncateText(audit.job_id ? String(audit.job_id) : '', 20) }}
              </td>
              <td class="col-action" :title="audit.action_type">
                <span class="actionBadge" :class="getActionBadgeClass(audit.action_type)">
                  {{ truncateText(audit.action_type || '', 15) }}
                </span>
              </td>
              <!-- Updated action details column with word wrap instead of truncate -->
              <td class="col-action-details word-wrap" :title="audit.message">
                {{ audit.message || '' }}
              </td>
              <td class="col-file-name word-wrap" :title="audit.uploaded_file_name">
                {{ audit.uploaded_file_name || '' }}
              </td>
              <td class="col-result-doc word-wrap" :title="audit.result_doc_file_name">
                {{ audit.result_doc_file_name || '' }}
              </td>
              <td class="col-changed" :title="formatChangedValue(audit.changed_value)">
                <pre v-if="isJson(audit.changed_value)" class="jsonPre">{{ formatChangedValue(audit.changed_value) }}</pre>
              </td>
              <td class="col-action-by word-wrap" :title="audit.performed_by_email">
                {{ audit.performed_by_email || '' }}
              </td>

              <td class="col-action-date word-wrap" :title="formatDate(audit.created_at)">
                {{ formatDate(audit.created_at) || '' }}
              </td>
            </tr>
            <tr v-if="auditData.length === 0" class="no-data-row">
              <td colspan="8" class="no-data-message">
                <div class="no-data-container">
                  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <h3>{{ $t('audit.noDataAvailable') }}</h3>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { get } from '@/utils/api'
import { showToast } from '../../utils/toast.js'
import { useTenantStore } from '@/stores/tenantStore'
export default {
  name: 'AuditReport',
  data() {
    return {
      loading: false,
      auditData: [],
      readPermission: false,
      writePermission: false,
      isPermissionDenied: false,
      filters: {
        jobId: '',
        fileName: '',
        resultDocument: '',
        fromDate: '',
        toDate: '',
        action: '',
      },
      actionOptions: [
        { value: 'JOB_CREATED', label: 'reports.actions.job_created' },
        { value: 'JOB_STARTED', label: 'reports.actions.job_started' },
        { value: 'JOB_DELETED', label: 'reports.actions.job_deleted' },
        { value: 'JOB_RETRIED', label: 'reports.actions.job_retried' },
        { value: 'JOB_VIEWED', label: 'reports.actions.job_viewed' },
        { value: 'STARTED_ENTITY_EXTRACTION', label: 'reports.actions.started_entity_extraction' },
        { value: 'DOCUMENT_VIEWED', label: 'reports.actions.document_viewed' },
        { value: 'FILE_UPLOADED', label: 'reports.actions.file_uploaded' },
        { value: 'VASION_IMPORTED', label: 'reports.actions.vasion_imported' },
        { value: 'SENT_TO_VASION', label: 'reports.actions.sent_to_vasion' },
        { value: 'DOCUMENT_PRINTED', label: 'reports.actions.document_printed' },
        { value: 'DOCUMENT_FIELD_MODIFIED', label: 'reports.actions.document_field_modified' },
        { value: 'USER_REGISTRATION', label: 'reports.actions.user_registration' },
        { value: 'USER_LOGIN', label: 'reports.actions.user_login' },
        { value: 'USER_LOGIN_MFA', label: 'reports.actions.user_login_mfa' },
        { value: 'DOCUMENT_TYPE_MODIFIED', label: 'reports.actions.document_type_modified' },
        { value: 'DOCUMENT_SPLIT', label: 'reports.actions.document_split' },
        { value: 'DATA_RETENTION_EXECUTED', label: 'reports.actions.data_retention_executed' },
        { value: 'FILES_DELETED', label: 'reports.actions.files_deleted' },
        { value: 'DOCUMENT_ENTITIES_DELETED', label: 'reports.actions.document_entities_deleted' },
        { value: 'JOB_DATA_DELETED', label: 'reports.actions.job_data_deleted' },
        { value: 'RESULT_DOCUMENT_DELETED', label: 'reports.actions.result_document_deleted' },
        { value: 'DEFAULT_VALUE_EDITED', label: 'reports.actions.DEFAULT_VALUE_EDITED' }
      ],
      page: 1,
      pageSize: 100,
      totalPages: 1,
      total: 0,
      pageSizeOptions: [10, 20, 50, 100],
      showPageSizeDropdown: false,
      downloadingPDF: false,
      downloadingExcel: false,
    }
  },
  async mounted() {
    await this.checkPermissions()
    if (!this.isPermissionDenied) {
      await this.fetchAudit()
    }
    document.addEventListener('click', this.handleClickAway)

    // Ensure store is fully initialized
    await useTenantStore()
    await this.$nextTick()
    await this.checkConfigAndHandleActions()
  },
  created() {
    // Get user data from localStorage
    const userDataString = localStorage.getItem('UserData')

    if (userDataString) {
      this.userData = JSON.parse(userDataString)
      // Initialize tenant store with user's tenant ID
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickAway)
  },
  methods: {
    async checkPermissions() {
      const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
      this.readPermission = permissions.some((p) => p.object_name === 'AUDIT_LOG_RD' && p.is_active)
      this.writePermission = permissions.some((p) => p.object_name === 'AUDIT_LOG_WR' && p.is_active)

      if (!this.readPermission) {
        this.isPermissionDenied = true
        return
      }

      this.isPermissionDenied = false
    },
    async checkConfigAndHandleActions() {
      const store = await useTenantStore()
      const userRolesString = localStorage.getItem('UserRoles')
      const userRoles = JSON.parse(userRolesString)
      const userRole = userRoles[0]?.role?.roleName || ''
      console.log('userRole===>', userRole)
      if (store?.tenantConfigs?.length) {
        const aiMethod = store.tenantConfigs.find((e) => e.key === 'ai_method')?.value
        if (userRole === 'Super Admin') {
          const superAdminActions = [
            { value: 'BDA_CLASSIFICATION_COMPLETED', label: 'BDA Classification Completed' },
            { value: 'BDA_EXTRACTION_COMPLETED', label: 'BDA Extraction Completed' },
            { value: 'TEXTRACT_EXTRACTION_COMPLETED', label: 'Textract Extraction Completed' },
            { value: 'COMPREHEND_CLASSIFICATION_COMPLETED', label: 'Comprehend Classification Completed' },
            { value: 'COMPREHEND_EXTRACTION_COMPLETED', label: 'Comprehend Extraction Completed' },
            { value: 'LINE_ITEM_MODIFIED', label: 'Line Item Modified' }
          ]
          this.actionOptions.push(...superAdminActions)
        } else if (aiMethod === 'Bedrock Data Automation') {
          const aiMethodBaseActions = [
            { value: 'BDA_CLASSIFICATION_COMPLETED', label: 'BDA Classification Completed' },
            { value: 'BDA_EXTRACTION_COMPLETED', label: 'BDA Extraction Completed' },
          ]
          this.actionOptions.push(...aiMethodBaseActions)
        } else if (aiMethod === 'Textract') {
          const aiMethodBaseActions = [
            { value: 'TEXTRACT_EXTRACTION_COMPLETED', label: 'Textract Extraction Completed' },
            { value: 'LINE_ITEM_MODIFIED', label: 'Line item Modified' }
          ]
          this.actionOptions.push(...aiMethodBaseActions)
        } else if (aiMethod === 'Custom Comprehend - Realtime' || aiMethod === 'Custom Comprehend - Async' || aiMethod === 'Custom Comprehend') {
          const aiMethodBaseActions = [
            { value: 'COMPREHEND_CLASSIFICATION_COMPLETED', label: 'Comprehend Classification Completed' },
            { value: 'COMPREHEND_EXTRACTION_COMPLETED', label: 'Comprehend Extraction Completed' },
          ]
          this.actionOptions.push(...aiMethodBaseActions)
        }
      }
    },

    async fetchAudit() {
      this.loading = true
      try {
        const params = {
          page: this.page,
          pageSize: this.pageSize,
          ...this.filters,
        }

        // Remove empty filter values
        Object.keys(params).forEach((key) => {
          if (params[key] === '' || params[key] === null || params[key] === undefined) {
            delete params[key]
          }
        })

        const queryString = new URLSearchParams(params).toString()
        const res = await get(`/v1/mx_audit_report_log?${queryString}`)

        if (res?.data) {
          this.auditData = res.data.data || []
          this.total = res.data.totalCount || 0
          this.totalPages = Math.ceil(this.total / this.pageSize)
        }
      } catch (err) {
        this.auditData = []
        this.total = 0
        this.totalPages = 1
      } finally {
        this.loading = false
      }
    },

    handleFilterChange() {
      // Debounced filter change can be implemented here if needed
    },
    handleClickAway(event) {
      const dropdown = this.$refs.dropdownEl
      if (dropdown && !dropdown.contains(event.target)) {
        this.showPageSizeDropdown = false
      }
    },

    togglePageSizeDropdown() {
      this.showPageSizeDropdown = !this.showPageSizeDropdown
    },

    selectPageSize(size) {
      this.pageSize = size
      this.page = 1

      this.showPageSizeDropdown = false
      this.fetchAudit()
    },

    goToPreviousPage() {
      if (this.page > 1) {
        this.page--
        this.fetchAudit()
      }
    },

    goToNextPage() {
      if (this.page < this.totalPages) {
        this.page++
        this.fetchAudit()
      }
    },

    handleSearch() {
      this.page = 1
      this.fetchAudit()
    },

    resetAllFilters() {
      this.filters = {
        jobId: '',
        fileName: '',
        resultDocument: '',
        fromDate: '',
        toDate: '',
        action: '',
      }
      this.page = 1
      this.fetchAudit()
    },

    truncateText(text, maxLength) {
      if (!text) return ''
      return text.length <= maxLength ? text : text?.slice(0, maxLength) + '...'
    },

    getActionBadgeClass(action) {
      const actionClasses = {
        // Job-related actions
        JOB_CREATED: 'badge-success',
        JOB_STARTED: 'badge-info',
        JOB_DELETED: 'badge-danger',
        JOB_RETRIED: 'badge-warning',
        JOB_VIEWED: 'badge-primary',
        STARTED_ENTITY_EXTRACTION: 'badge-info',

        // Document-related actions
        DOCUMENT_VIEWED: 'badge-info',
        FILE_UPLOADED: 'badge-success',
        VASION_IMPORTED: 'badge-info',
        SENT_TO_VASION: 'badge-info',
        DOCUMENT_PRINTED: 'badge-success',

        // Entity-related actions
        DOCUMENT_FIELD_MODIFIED: 'badge-success',
        DOCUMENT_TYPE_MODIFIED: 'badge-warning',
        DOCUMENT_SPLIT: 'badge-primary',
        FILES_DELETED: 'badge-danger',
        DOCUMENT_ENTITIES_DELETED: 'badge-danger',
        RESULT_DOCUMENT_DELETED: 'badge-danger',

        // Auth-related actions
        USER_REGISTRATION: 'badge-success',
        USER_LOGIN: 'badge-primary',
        USER_LOGIN_MFA: 'badge-warning',

        // Data retention
        DATA_RETENTION_EXECUTED: 'badge-success',
        JOB_DATA_DELETED: 'badge-danger',
      }

      return actionClasses[action] || 'badge-secondary'
    },

    formatDate(dateStr) {
      if (!dateStr) return ''

      const date = new Date(dateStr)
      const month = date.toLocaleString('default', { month: 'long' })
      const day = date.getDate()
      const year = date.getFullYear()

      function getOrdinalSuffix(n) {
        if (n > 3 && n < 21) return 'th'
        switch (n % 10) {
          case 1:
            return 'st'
          case 2:
            return 'nd'
          case 3:
            return 'rd'
          default:
            return 'th'
        }
      }

      const formattedDay = day + getOrdinalSuffix(day)
      const time = date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })

      return `${month} ${formattedDay} ${year}, ${time}`
    },

    downloadToExcel() {
      // To be implemented
    },

    isJson(str) {
      if (typeof str !== 'string') return false
      try {
        const parsed = JSON.parse(str)
        return typeof parsed === 'object' && parsed !== null
      } catch (e) {
        return false
      }
    },

    formatChangedValue(value) {
      if (this.isJson(value)) {
        return JSON.stringify(JSON.parse(value), null, 2)
      }
      return value
    },
    async downloadAsPDF() {
      if (!this.writePermission) {
        showToast({
          title: this.$t('common.no_permission'),
          message: this.$t('reports.no_write_permission'),
          type: 'error',
        })
        return
      }
      // Show loading state (you can add a loading spinner here)
      this.downloadingPDF = true

      try {
        const params = {
          page: 1,
          pageSize: 2500,
          ...this.filters,
        }

        // Remove empty filter values
        Object.keys(params).forEach((key) => {
          if (params[key] === '' || params[key] === null || params[key] === undefined) {
            delete params[key]
          }
        })

        const queryString = new URLSearchParams(params).toString()
        const res = await get(`/v1/mx_audit_report_log?${queryString}`)

        let allAuditData = []

        if (res?.data) {
          allAuditData = res.data.data || []
        }

        if (allAuditData.length === 0) {
          showToast({
            title: this.$t('reports.toast.no_data'),
            message: this.$t('reports.toast.no_audit_data'),
            type: 'warning',
          })
          return
        }
        // Generate PDF content
        this.generatePDF(allAuditData)
      } catch (error) {
        showToast({
          title: this.$t('reports.toast.error'),
          message: error.response?.data?.message || this.$t('reports.toast.failed_download'),
          type: 'error',
        })
      } finally {
        this.downloadingPDF = false
      }
    },

    generatePDF(auditData) {
      const logoBase64 = '/footer-logo_vt.png' // Replace with full Base64 string

      const htmlContent = `
    <html>
      <head>
        <title>Audit Report</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
            font-size: 12px;
          }
          .report-header {
            display: flex;
            flex-direction: column;
            align-items: center;
            margin-bottom: 20px;
          }
          .header-top {
            display: flex;
            align-items: center;
            gap: 10px;
          }
          .header-top img {
            height: 30px;
          }
          .header-title {
            color: #164863;
            font-size: 20px;
            font-weight: bold;
          }
          .header-bottom {
            margin-top: 5px;
            font-size: 10px;
            color: #666;
            text-align: center;
          }

          .filters-info {
            background-color: #f8f9fa;
            padding: 15px;
            border-radius: 5px;
            margin-bottom: 20px;
            border-left: 4px solid #164863;
          }
          .filters-info h3 {
            margin: 0 0 10px 0;
            color: #164863;
            font-size: 14px;
          }
          .filter-item {
            display: inline-block;
            margin-right: 20px;
            margin-bottom: 5px;
          }
          .filter-label {
            font-weight: bold;
            color: #555;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
            font-size: 10px;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
            word-wrap: break-word;
          }
          th {
            background-color: #f8f9fa;
            font-weight: bold;
            color: #164863;
            font-size: 11px;
          }
          tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          .action-column { width: 15%; }
          .date-column { width: 18%; }
          .message-column { width: 35%; }
          .user-column { width: 18%; }
          .job-column { width: 8%; }
          .file-column { width: 6%; }
          .action-badge {
            padding: 2px 6px;
            border-radius: 3px;
            font-size: 9px;
            font-weight: bold;
            color: white;
          }
          .badge-success { background-color: #28a745; }
          .badge-info { background-color: #17a2b8; }
          .badge-danger { background-color: #dc3545; }
          .badge-warning { background-color: #ffc107; color: #212529; }
          .badge-primary { background-color: #007bff; }
          .badge-secondary { background-color: #6c757d; }
          .badge-default { background-color: #e9ecef; color: #495057; }
        </style>
      </head>
      <body>
   <div class="report-header">
  <div class="header-top">
    <img src="${logoBase64}" alt="Logo" />
    <div class="header-title">Audit Report</div>
  </div>
  <div class="header-bottom">
    <p>Generated on: ${new Date().toLocaleString()}</p>
    <p>Exported by: ${this.userData?.email_id || '---'}</p>
    <p>Total Records: ${auditData.length}</p>
  </div>
</div>


        ${this.generateFiltersInfo()}

        <table>
          <thead>
            <tr>
              <th class="job-column">${this.$t('audit.pdfHeaders.jobId')}</th>
              <th class="date-column">${this.$t('audit.pdfHeaders.dateTime')}</th>
              <th class="action-column">${this.$t('audit.pdfHeaders.actionType')}</th>
              <th class="message-column">${this.$t('audit.pdfHeaders.message')}</th>
              <th class="user-column">${this.$t('audit.pdfHeaders.performedBy')}</th>
              <th class="file-column">${this.$t('audit.pdfHeaders.fileId')}</th>
            </tr>
          </thead>
          <tbody>
            ${auditData
              .map(
                (audit) => `
              <tr>
                <td>${audit.job_id || '---'}</td>
                <td>${this.formatDate(audit.created_at)}</td>
                <td>
                  <span class="action-badge ${this.getActionBadgeClass(audit.action_type)}">
                    ${audit.action_type || '---'}
                  </span>
                </td>
                <td>${audit.message || '---'}</td>
                <td>${audit.performed_by_email || '---'}</td>
                <td>${audit.file_id || '---'}</td>
              </tr>
            `
              )
              .join('')}
          </tbody>
        </table>
      </body>
    </html>
  `

      const printWindow = window.open('', '_blank')
      printWindow.document.write(htmlContent)
      printWindow.document.close()
      printWindow.focus()
      printWindow.print()

      showToast({
        title: this.$t('reports.toast.success'),
        message: this.$t('reports.toast.pdf_exported'),
        type: 'success',
      })
    },
    generateFiltersInfo() {
      const activeFilters = []

      if (this.filters.jobId) activeFilters.push(`Job ID: ${this.filters.jobId}`)
      if (this.filters.fileName) activeFilters.push(`File Name: ${this.filters.fileName}`)
      if (this.filters.resultDocument) activeFilters.push(`Result Document: ${this.filters.resultDocument}`)
      if (this.filters.fromDate) activeFilters.push(`From Date: ${this.filters.fromDate}`)
      if (this.filters.toDate) activeFilters.push(`To Date: ${this.filters.toDate}`)
      if (this.filters.action) {
        const actionLabel = this.actionOptions.find((opt) => opt.value === this.filters.action)?.label || this.filters.action
        activeFilters.push(`Action: ${actionLabel}`)
      }

      // if (activeFilters.length === 0) {
      //   return '<div class="filters-info"><h3>Filters Applied:</h3><p>No filters applied - showing all records</p></div>'
      // }

      if (activeFilters.length === 0) {
        return ''
      }

      return `
    <div class="filters-info">
      <h3>Filters Applied:</h3>
      ${activeFilters.map((filter) => `<div class="filter-item"><span class="filter-label">${filter}</span></div>`).join('')}
    </div>
  `
    },
    async downloadAsExcel() {
      if (!this.writePermission) {
        showToast({
          title: this.$t('common.no_permission'),
          message: this.$t('reports.no_write_permission'),
          type: 'error',
        })
        return
      }

      // Show loading state (you can add a loading spinner here)
      this.downloadingExcel = true

      try {
        // Prepare params for fetching all data
        const params = {
          page: 1, // Always fetch from page 1 since we're getting all data
          pageSize: 1000000, // 10 lakh records
          ...this.filters, // Include current filters
        }

        // Remove empty filter values
        Object.keys(params).forEach((key) => {
          if (params[key] === '' || params[key] === null || params[key] === undefined) {
            delete params[key]
          }
        })

        const queryString = new URLSearchParams(params).toString()
        const res = await get(`/v1/mx_audit_report_log?${queryString}`)

        let allAuditData = []
        if (res?.data) {
          allAuditData = res.data.data || []
        }

        if (allAuditData.length === 0) {
          showToast({
            title: this.$t('reports.toast.no_data'),
            message: this.$t('reports.toast.no_audit_data'),
            type: 'warning',
          })
          return
        }

        // Generate Excel content
        this.generateExcel(allAuditData)
      } catch (error) {
        showToast({
          title: this.$t('reports.toast.error'),
          message: error.response?.data?.message || this.$t('reports.toast.failed_download'),
          type: 'error',
        })
      } finally {
        this.downloadingExcel = false
      }
    },

    generateExcel(auditData) {
      // Define headers for Excel file
      const headers = [this.$t('audit.excelHeaders.id'), this.$t('audit.excelHeaders.jobId'), this.$t('audit.excelHeaders.dateTime'), this.$t('audit.excelHeaders.actionType'), this.$t('audit.excelHeaders.message'), this.$t('audit.excelHeaders.performedBy'), this.$t('audit.excelHeaders.fileId'), this.$t('audit.excelHeaders.jobStatus'), this.$t('audit.excelHeaders.vasionStatus'), this.$t('audit.excelHeaders.uploadedFileName'), this.$t('audit.excelHeaders.resultDocumentId'), this.$t('audit.excelHeaders.entityId'), this.$t('audit.excelHeaders.tenantId'), this.$t('audit.excelHeaders.performedById'), this.$t('audit.excelHeaders.changedValue')]

      // Generate CSV content
      const csvContent = [
        headers.join(','),
        ...auditData.map((audit) =>
          [
            `"${audit.id || ''}"`,
            `"${audit.job_id || ''}"`,
            `"${this.formatDate(audit.created_at)}"`,
            `"${audit.action_type || ''}"`,
            `"${(audit.message || '').replace(/"/g, '""')}"`, // Escape quotes in message
            `"${audit.performed_by_email || ''}"`,
            `"${audit.file_id || ''}"`,
            `"${audit.job_status || ''}"`,
            `"${audit.job_vasion_status || ''}"`,
            `"${audit.uploaded_file_name || ''}"`,
            `"${audit.result_document_id || ''}"`,
            `"${audit.entity_id || ''}"`,
            `"${audit.tenant_id || ''}"`,
            `"${audit.performed_by || ''}"`,
            `"${(audit.changed_value || '').replace(/"/g, '""')}"`, // Escape quotes in changed_value
          ].join(',')
        ),
      ].join('\n')

      // Create and download the file
      const blob = new Blob(['\uFEFF' + csvContent], {
        type: 'text/csv;charset=utf-8;',
      }) // \uFEFF is BOM for UTF-8

      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)

      // Generate filename with current date and applied filters info
      const currentDate = new Date().toISOString().split('T')[0]
      const filterSuffix = this.getFilterSuffix()
      link.setAttribute('download', `audit_report_${currentDate}${filterSuffix}.csv`)

      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Clean up the URL object
      URL.revokeObjectURL(url)

      showToast({
        title: this.$t('reports.toast.success'),
        message: this.$t('reports.toast.excel_exported'),
        type: 'success',
      })
    },

    getFilterSuffix() {
      const activeFilters = []

      if (this.filters.jobId) activeFilters.push(`job_${this.filters.jobId}`)
      if (this.filters.action) activeFilters.push(`action_${this.filters.action}`)
      if (this.filters.fromDate) activeFilters.push(`from_${this.filters.fromDate}`)
      if (this.filters.toDate) activeFilters.push(`to_${this.filters.toDate}`)

      return activeFilters.length > 0 ? `_${activeFilters.join('_')}` : ''
    },
  },
}
</script>

<style scoped>
@import '@/styles/jobHistory.css';

/* Permission Denied Styles */
.permission-denied {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #f8f9fa;
}

.permission-denied-content {
  text-align: center;
  padding: 40px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-width: 500px;
}

.permission-denied-content h2 {
  color: #dc3545;
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
}

.permission-denied-content p {
  color: #666;
  font-size: 16px;
  line-height: 1.5;
}

* {
  box-sizing: border-box;
}

.auditReportContainer {
  width: 100%;
  max-width: 100vw;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.auditContent {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  max-width: 100%;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.auditFiltersContainer {
  flex-shrink: 0;
  margin: 5px 0 15px 0;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  max-width: 100%;
}

.filterRow {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: flex-end;
}

.filterRow:last-child {
  margin-bottom: 0;
}

.filterGroup {
  flex: 1;
  min-width: 0;
  max-width: 100%;
}

.filterButtonGroup {
  display: flex;
  gap: 1rem;
  flex-shrink: 0;
  min-width: auto;
  flex: none;
}

.filterLabel {
  display: block;
  margin-bottom: 6px;
  color: #344054;
  font-family: Lato, sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25rem;
}

.filterInput {
  width: 100%;
  height: 2.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  border: 1px solid #d0d5dd;
  background: #fff;
  font-family: Lato, sans-serif;
  font-size: 0.875rem;
  color: #344054;
  transition: border-color 0.2s;
}

.filterInput:focus {
  outline: none;
  border-color: #7ac4eb;
  box-shadow: 0 0 0 2px rgba(122, 196, 235, 0.2);
}



.dropdownWrapper {
  position: relative;
}

.dropdownInput {
  appearance: none;
  padding-right: 2.25rem;
  cursor: pointer;
}

.dropdownIcon {
  position: absolute;
  right: 0.625rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}

.resetDateButton {
  height: 2.5rem;
  padding: 0 1.5rem;
  border-radius: 6px;
  border: none;
  background: var(--primary-color);
  color: #fff;
  font-family: Lato, sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  margin-top: 1.5625rem;
  white-space: nowrap;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.resetDateButton:hover {
  background: var(--primary-color);
  box-shadow: 0 4px 8px rgba(22, 72, 99, 0.3);
}

.paginationControls {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  min-width: 0;
}

.downloadGroup {
  flex-shrink: 0;
}

.pageOfTitle {
  font-family: Lato, sans-serif;
  font-size: 0.875rem;
  color: #667085;
  white-space: nowrap;
}

.pageDiv {
  position: relative;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #ccc;
  padding: 6px 12px;
  border-radius: 4px;
  background: #fff;
}

.word-wrap {
  white-space: normal !important;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 200px;
  /* Adjust as needed */
  line-height: 1.4;
}

.downloadExcelButton {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 2rem;
  padding: 0 1.5rem;
  border-radius: 6px;
  border: none;
  background: var(--primary-color);
  color: #fff;
  font-family: Lato, sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  box-shadow: 0 2px 4px rgba(22, 72, 99, 0.2);
  white-space: nowrap;
  flex-shrink: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.downloadExcelButton:hover {
  background: var(--primary-color);
  box-shadow: 0 4px 8px rgba(22, 72, 99, 0.3);
}

.downloadExcelButton svg {
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
}

.tableContainer {
  flex: 1;
  min-height: 0;
  overflow: auto;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  max-width: 100%;
}

.customTable {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  table-layout: fixed;
  max-width: 100%;
  word-wrap: break-word;
}

.customTableHeader {
  background: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 10;
}

.customTableHeader th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-family: Lato, sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  color: #344054;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

tbody {
  background: #fff;
}

.tableRow {
  height: 3.125rem;
  border-bottom: 1px solid #f2f4f7;
}

.tableRow:hover {
  background-color: var(--secondary-color);
}

tbody td {
  padding: 0.75rem 1rem;
  font-family: Lato, sans-serif;
  font-size: 0.875rem;
  color: #667085;
  vertical-align: top;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-job-id {
  width: 9%;
}

.col-action {
  width: 15%;
}

.col-action-details {
  width: 18%;
}

.col-file-name {
  width: 15%;
}

.col-result-doc {
  width: 15%;
}

.col-changed {
  width: 15%;
}

.col-action-by {
  width: 13%;
}

.col-action-date {
  width: 15%;
}

.jsonPre {
  white-space: pre-wrap;
  word-break: break-word;
  font-family: monospace;
  font-size: 0.75rem;
  margin: 0;
  padding: 0.25rem;
  background: #f8f9fa;
  border-radius: 4px;
  max-height: 6.25rem;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
}

.actionBadge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  white-space: nowrap;
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.badge-success {
  background: #d1fae5;
  color: #065f46;
}

.badge-info {
  background: #dbeafe;
  color: #1e40af;
}

.badge-danger {
  background: #fee2e2;
  color: #dc2626;
}

.badge-warning {
  background: #fef3c7;
  color: #d97706;
}

.badge-primary {
  background: #e0f2fe;
  color: #0277bd;
}

.badge-secondary {
  background: #f3f4f6;
  color: #4b5563;
}

.badge-default {
  background: #f9fafb;
  color: #6b7280;
}

.no-data-row {
  height: 12.5rem;
}

.no-data-message {
  text-align: center;
  color: #9ca3af;
}

.no-data-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}

.no-data-container h3 {
  font-family: Lato, sans-serif;
  font-size: 1.125rem;
  font-weight: 600;
  color: #6b7280;
  margin: 0;
}

.tableContainer::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.tableContainer::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.tableContainer::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.tableContainer::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.tableContainer::-webkit-scrollbar-corner {
  background: #f1f1f1;
}

/* Responsive breakpoints */
@media (max-width: 1200px) {
  .filterGroup {
    min-width: calc(33.333% - 0.67rem);
  }
}

@media (max-width: 900px) {
  .filterGroup {
    min-width: calc(50% - 0.5rem);
  }
}

@media (max-width: 768px) {
  .customTable {
    table-layout: auto;
  }

  .col-job-id {
    width: 100px;
  }

  .col-action {
    width: 100px;
  }

  .col-action-details {
    width: 150px;
  }

  .col-file-name {
    width: 120px;
  }

  .col-result-doc {
    width: 120px;
  }

  .col-changed {
    width: 120px;
  }

  .col-action-by {
    width: 120px;
  }

  .col-action-date {
    width: 140px;
  }

  .auditFiltersContainer {
    padding: 0.75rem;
  }

  .filterRow {
    gap: 0.75rem;
  }

  .paginationControls {
    justify-content: center;
    order: 2;
  }

  .downloadGroup {
    order: 1;
    align-self: stretch;
  }

  .downloadExcelButton {
    width: 100%;
    justify-content: center;
  }

  .pageOfTitle {
    display: none;
  }
}

@media (max-width: 600px) {
  .filterGroup {
    min-width: 100%;
  }

  .filterButtonGroup {
    width: 100%;
    justify-content: stretch;
  }

  .filterButtonGroup .resetDateButton {
    flex: 1;
  }
}

@media (max-width: 480px) {
  .auditFiltersContainer {
    padding: 0.5rem;
  }

  .paginationControls {
    gap: 0.5rem;
  }

  .pageOfTitle {
    font-size: 0.75rem;
  }

  .customTableHeader th {
    padding: 0.5rem 0.75rem;
    font-size: 0.75rem;
  }

  tbody td {
    padding: 0.5rem 0.75rem;
    font-size: 0.75rem;
  }

  /* Key differences and fixes for the page size dropdown */

  /* 1. Your current code is missing the proper dropdown positioning styles */
  .pageSizeDropdown,
  .pageSizeDropdownNew {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 1000;
    background: #fff;
    border: 1px solid #ccc;
    border-radius: 4px;
    margin-top: 4px;
    list-style: none;
    padding: 4px 0;
    width: 100%;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    /* Enhanced shadow */
    min-width: 80px;
    /* Ensure minimum width */
  }

  .pageSizeDropdown li,
  .pageSizeDropdownNew li {
    padding: 8px 12px;
    /* Increased padding for better touch targets */
    cursor: pointer;
    font-family: Lato, sans-serif;
    /* Use Lato instead of Archivo */
    font-size: 14px;
    color: #344054;
    /* Use consistent color scheme */
    transition: background-color 0.2s ease;
  }

  .pageSizeDropdown li:hover,
  .pageSizeDropdownNew li:hover {
    background-color: #f5f5f5;
  }

  /* 2. Ensure pageDiv has proper positioning */

  .pageDiv:hover {
    border-color: var(--primary-color);
    /* Add hover effect */
  }

  /* 3. Arrow styling */
  .arrowDownNew {
    width: 16px;
    height: 16px;
    opacity: 0.6;
    transition: transform 0.2s ease;
  }

  .pageDiv:hover .arrowDownNew {
    opacity: 1;
  }

  /* 4. Ensure proper layout for the customRow */
  .customRow {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
    justify-content: flex-start;
    /* Changed from space-between */
    flex-wrap: wrap;
    /* Allow wrapping on smaller screens */
  }

  /* 5. Page navigation styling */
  .pageOfTitle {
    font-family: Lato, sans-serif;
    font-size: 14px;
    color: #667085;
    white-space: nowrap;
    margin: 0 8px;
    /* Add some spacing */
  }

  /* 6. Download button positioning */
  .downloadExcelButton {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    /* Consistent with other controls */
    padding: 0 16px;
    border-radius: 6px;
    border: none;
    background: #164863;
    color: #fff;
    font-family: Lato, sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
    box-shadow: 0 2px 4px rgba(22, 72, 99, 0.2);
    margin-left: auto;
    /* Push to the right */
  }

  .downloadExcelButton:hover {
    background: var(--primary-color);
    box-shadow: 0 4px 8px rgba(22, 72, 99, 0.3);
  }

  /* 7. Responsive adjustments */
  @media (max-width: 768px) {
    .customRow {
      justify-content: center;
      gap: 12px;
    }

    .pageOfTitle {
      display: none;
      /* Hide on mobile */
    }

    .downloadExcelButton {
      margin-left: 0;
      order: -1;
      /* Move to beginning on mobile */
      width: 100%;
      justify-content: center;
    }

    .download-info-note-inline {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      background: #eff6ff;
      border: 1px solid #bfdbfe;
      border-radius: 6px;
      font-family: Lato, sans-serif;
      font-size: 0.75rem;
      color: #1e40af;
      white-space: nowrap;
      margin-left: auto;
      margin-right: 12px;
    }

    .download-info-note-inline svg {
      flex-shrink: 0;
      color: #3b82f6;
    }

    @media (max-width: 1024px) {
      .download-info-note-inline span {
        display: none;
      }

      .download-info-note-inline {
        padding: 6px;
        background: transparent;
        border: none;
        margin-right: 8px;
      }

      .download-info-note-inline svg {
        width: 20px;
        height: 20px;
        cursor: help;
      }
    }

    @media (max-width: 768px) {
      .download-info-note-inline {
        order: -2;
        margin-left: 0;
      }
    }
  }
}
</style>
